/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { BackArrow, Close } from '@ahamove/icons';
import { formatCurrency, isLink } from '@ahm/common-helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Radio,
  SimpleGrid,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { closeAllModals } from '@mantine/modals';
import { Fragment, useCallback, useMemo, useState, useTransition } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import VPBankPrepaidTopup from './components/vpbank-prepaid-topup';
import AhaNumberInput from '@/components/common/AhaNumberInput';
import { ModalName, showModal } from '@/components/modals';
import type { ModalCheckoutProps } from '@/components/modals/modal-checkout';
import PaymentSelectionItem from '@/components/modals/payment-modal/components/payment-selection-item';
import { usePayMutation } from '@/features/checkout/payment/pay-mutation';
import { useGetCheckoutPaymentMethod } from '@/features/checkout/payment/use-get-checkout-payment-methods';
import { CheckoutType } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type TopupFormData = {
  topupInput: number | undefined;
  selectedPaymentMethod: string | undefined;
};

type PaymentMethod = {
  code: string;
  group_type: string;
  method_id: string;
  method_type: string;
  extra_data: any;
  enable: boolean;
  is_default: boolean;
  name_vi_vn: string;
  name_en_us: string;
  description_vi_vn?: string;
  description_en_us?: string;
  icon_url: string;
  is_corporate_method: boolean;
  is_support_tokenize: boolean;
  error_message_vi_vn: string;
  error_message_en_us: string;
  button_color: string;
};

export function TopupModal() {
  const isMobile = useIsMobile();
  const [isPendingTip] = useTransition();

  const [currentStep, setCurrentStep] = useState<
    'amount' | 'payment' | 'vpbank_prepaid'
  >('amount');

  const { data: paymentMethodList } = useGetCheckoutPaymentMethod('top-up');
  const topupFormSchema = z.object({
    topupInput: z
      .union([
        z.string().transform((val) => {
          if (val === '' || val === null || val === undefined) {
            return undefined;
          }
          const num = Number(val);
          return isNaN(num) ? undefined : num;
        }),
        z.number(),
        z.undefined(),
      ])
      .optional()
      .refine(
        (val) => {
          if (val !== undefined && val !== null) {
            return val >= 1000;
          }
          return true;
        },
        {
          message: 'Số tiền từ 1.000đ trở lên',
        }
      ),
    selectedPaymentMethod: z.string().optional(),
  });

  const methods = useForm<TopupFormData>({
    resolver: zodResolver(topupFormSchema),
    defaultValues: {
      topupInput: undefined,
      selectedPaymentMethod: undefined,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    clearErrors,
    setValue,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = methods;

  const topupAmount = watch('topupInput');
  const selectedPaymentMethod = watch('selectedPaymentMethod');

  const handleShowQrCode = useCallback(
    (innerProps: ModalCheckoutProps['innerProps']) => {
      showModal(ModalName.Checkout, innerProps, {
        fullScreen: isMobile,
        closeOnEscape: false,
        padding: 0,
      });
    },
    [isMobile]
  );

  const handlePaymentResponse = useCallback(
    (transaction: any, requestBody: number) => {
      if (!transaction) return;
      const paymentMethod = paymentMethodList?.find(
        (method) => method.method_id === selectedPaymentMethod
      );
      if (!paymentMethod) return;

      if (isMobile) {
        const link = transaction.pay_url ?? transaction.data;
        if (isLink(link)) {
          window.open(link, '_blank');
          return;
        }
      } else {
        handleShowQrCode({
          data: transaction,
          paymentMethod,
          requestBody,
          title: <>{requestBody?.title}</>,
          amount: requestBody?.amount ?? 0,
        });
      }
    },
    [paymentMethodList, selectedPaymentMethod, isMobile, handleShowQrCode]
  );

  const { mutateAsync: mutatePayment } = usePayMutation({
    onSuccess: (data, variables) => {
      if (variables.body) {
        handlePaymentResponse(data, variables.body);
      }
    },
    onError: (error) => {
      console.error('Payment error:', error);
    },
  });

  const onSubmit = async (data: TopupFormData) => {
    if (currentStep === 'amount' && data.topupInput) {
      setCurrentStep('payment');
      return;
    }

    if (
      currentStep === 'payment' &&
      data.selectedPaymentMethod &&
      data.topupInput
    ) {
      const paymentMethod = paymentMethodList?.find(
        (method) => method.method_id === data.selectedPaymentMethod
      );

      // Check if it's bank transfer (vpbank_prepaid)
      if (paymentMethod?.code === 'vpbank_prepaid') {
        setCurrentStep('vpbank_prepaid');
        return;
      }

      // Handle other payment methods
      try {
        if (!paymentMethod) return;

        const paymentBody = {
          title: 'Nạp tiền vào ví Aha',
          payment_type: CheckoutType.Topup as any,
          payment_provider: paymentMethod.code,
          amount: data.topupInput,
          description: 'Nạp tiền vào ví Aha',
        };

        await mutatePayment({
          body: paymentBody,
          checkoutType: 'top-up',
          isAutoDebit: false,
        });
      } catch (error) {
        console.error('Payment submission error:', error);
      }
    }

    if (currentStep === 'vpbank_prepaid') {
      console.log('Bank transfer completed');
      closeAllModals();
    }
  };

  const onError = (errors: any) => {
    console.error('Form validation errors:', errors);
  };

  const isDisableSubmit = useMemo(() => {
    if (currentStep === 'amount') {
      return !topupAmount || topupAmount < 1000;
    }
    if (currentStep === 'payment') {
      return !selectedPaymentMethod;
    }
    if (currentStep === 'vpbank_prepaid') {
      return false; // Always allow completion for bank transfer
    }
    return true;
  }, [currentStep, topupAmount, selectedPaymentMethod]);

  const handleTopupInputChange = (value: string | number | null) => {
    const numValue = Number(value);
    setValue('topupInput', numValue || undefined);
  };

  const renderPaymentMethods = () => {
    if (!paymentMethodList?.length) return null;

    return (
      <Box>
        <Radio.Group
          value={selectedPaymentMethod}
          onChange={(value) =>
            setValue('selectedPaymentMethod', value, { shouldValidate: true })
          }
        >
          <Stack gap={0}>
            {paymentMethodList.map((method: PaymentMethod, idx) => (
              <Fragment key={idx}>
                <PaymentSelectionItem
                  method={method}
                  onSelect={(methodId) =>
                    setValue('selectedPaymentMethod', methodId, {
                      shouldValidate: true,
                    })
                  }
                />
                {paymentMethodList?.length > idx + 1 && <Divider ml={55} />}
              </Fragment>
            ))}
          </Stack>
        </Radio.Group>
      </Box>
    );
  };

  return (
    <>
      <Group
        justify={'space-between'}
        px={rem(16)}
        w="100%"
        bg="white"
        pos={'sticky'}
        style={() => ({
          zIndex: 10,
          flexDirection: isMobile ? 'row-reverse' : 'row',
          borderBottom: `1px solid var(--mantine-color-neutral-2)`,
        })}
        h={60}
      >
        {/* {isMobile ? <Space mx={8} w={rem(44)} /> : null} */}

        {currentStep === 'payment' || currentStep === 'vpbank_prepaid' ? (
          <ActionIcon
            onClick={() =>
              setCurrentStep(
                currentStep === 'vpbank_prepaid' ? 'payment' : 'amount'
              )
            }
            style={{ outline: 'none !important' }}
            size={24}
            variant="transparent"
            c="neutral"
          >
            <BackArrow size={20} height={20} width={20} />
          </ActionIcon>
        ) : (
          <Space mx={8} w={rem(24)} />
        )}

        <Title order={4} ta="center">
          {currentStep === 'amount'
            ? 'Nạp tiền cho tài khoản'
            : currentStep === 'payment'
              ? 'Xác nhận thanh toán'
              : 'Thông tin chuyển khoản'}
        </Title>

        <ActionIcon
          onClick={() => closeAllModals()}
          style={{ outline: 'none !important' }}
          size={24}
          variant="transparent"
          c="neutral"
        >
          <Close color="black" size={24} height={24} width={24} />
        </ActionIcon>
      </Group>

      <FormProvider {...methods}>
        <Box
          pt={rem(24)}
          h={isMobile ? 'calc(100svh - 200px)' : rem(300)}
          mih="500px"
          w="100%"
          bg="white"
          style={{
            overflow: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Stack
            w="100%"
            px={currentStep === 'amount' ? rem(24) : 0}
            pb={currentStep !== 'vpbank_prepaid' ? rem(90) : rem(24)}
            gap={currentStep === 'amount' ? rem(24) : 0}
          >
            {currentStep === 'amount' ? (
              <>
                <Controller
                  name="topupInput"
                  control={control}
                  render={({ field }) => (
                    <>
                      <AhaNumberInput
                        value={field.value ?? undefined}
                        onChange={(value) => {
                          field.onChange(value);
                          handleTopupInputChange(value);
                        }}
                        label="Nhập số tiền bạn muốn nạp"
                        min={0}
                        thousandSeparator="."
                        decimalSeparator=","
                        error={errors.topupInput?.message}
                        className="w-full"
                        clearable
                        onClear={() => {
                          clearErrors('topupInput');
                          handleTopupInputChange(null);
                        }}
                      />
                    </>
                  )}
                />

                <SimpleGrid cols={3} spacing={rem(12)}>
                  {[
                    { value: 100000, name: '100k' },
                    { value: 200000, name: '200k' },
                    { value: 300000, name: '300k' },
                    { value: 400000, name: '400k' },
                    { value: 500000, name: '500k' },
                    { value: 600000, name: '600k' },
                  ].map((option) => (
                    <Button
                      key={option.name}
                      variant="outline"
                      bg={
                        methods.watch('topupInput') === option.value
                          ? 'primary.1'
                          : 'white'
                      }
                      c={
                        methods.watch('topupInput') === option.value
                          ? 'primary.5'
                          : 'neutral.7'
                      }
                      radius="md"
                      size="md"
                      w="100%"
                      styles={(theme) => ({
                        root: {
                          borderColor:
                            methods.watch('topupInput') === option.value
                              ? theme.colors.primary[7]
                              : theme.colors.neutral[2],
                        },
                      })}
                      onClick={() =>
                        methods.setValue('topupInput', option.value, {
                          shouldValidate: true,
                        })
                      }
                    >
                      {formatCurrency(option.value)}
                    </Button>
                  ))}
                </SimpleGrid>
              </>
            ) : currentStep === 'vpbank_prepaid' ? (
              <VPBankPrepaidTopup
                amount={topupAmount ?? 0}
                onComplete={() => {
                  console.log('Bank transfer completed');
                  closeAllModals();
                }}
                onBack={() => setCurrentStep('payment')}
                onClose={() => closeAllModals()}
              />
            ) : (
              <>
                {/* Section 1: Nội dung thanh toán */}
                <Box px={rem(24)} pb={rem(16)}>
                  <Text size="sm" fw={600} c="neutral.7" mb={rem(8)}>
                    NỘI DUNG THANH TOÁN
                  </Text>
                  <Box
                    bg="neutral.0"
                    p={rem(16)}
                    style={{
                      borderRadius: rem(8),
                      border: '1px solid var(--mantine-color-neutral-2)',
                    }}
                  >
                    <Group justify="space-between" align="center">
                      <Text size="md" c="neutral.9">
                        Nạp tiền vào tài khoản chính
                      </Text>
                      <Text size="md" fw={600} c="neutral.9">
                        {formatCurrency(topupAmount ?? 0)}
                      </Text>
                    </Group>
                  </Box>
                </Box>

                {/* Section 2: Chọn phương thức thanh toán */}
                <Box px={rem(24)}>
                  <Text size="sm" fw={600} c="neutral.7" mb={rem(8)}>
                    CHỌN PHƯƠNG THỨC THANH TOÁN
                  </Text>
                </Box>

                {/* Section 3: Payment Methods */}
                {renderPaymentMethods()}
              </>
            )}
          </Stack>
        </Box>

        {/* Submit button */}
        {currentStep !== 'vpbank_prepaid' && (
          <Box
            pos="fixed"
            bottom={0}
            left={0}
            right={0}
            bg="white"
            p={rem(16)}
            style={{
              zIndex: 10,
              borderTop: '1px solid #e9ecef',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Button
              fullWidth
              h={rem(48)}
              fw={700}
              onClick={handleSubmit(onSubmit, onError)}
              loading={isPendingTip}
              disabled={isDisableSubmit}
            >
              {currentStep === 'amount' ? 'Tiếp tục' : 'Thanh toán'}
            </Button>
          </Box>
        )}
      </FormProvider>
    </>
  );
}
